import {
  Link,
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState
} from '@backstage/core-components';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar
} from '@backstage/plugin-user-settings';
import { makeStyles } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CategoryIcon from '@material-ui/icons/Category';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import DomainIcon from '@material-ui/icons/Domain'
import HomeIcon from '@material-ui/icons/Home';
import MenuIcon from '@material-ui/icons/Menu';
import GroupIcon from '@material-ui/icons/People';
import FeedbackIcon from '@material-ui/icons/Feedback';
import HistoryIcon from '@material-ui/icons/History';
import BugReportIcon from '@material-ui/icons/BugReport';
import BuildIcon from '@material-ui/icons/Build';
import React, { PropsWithChildren } from 'react';
import { TotvsLogoFull, TotvsLogoIcon } from '../themes';
import { Administration } from '@backstage-community/plugin-rbac';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { openControlReadPermission } from '@internal/backstage-plugin-supply-features-common';
import { requestManagerReadPermission } from '@internal/backstage-plugin-request-manager-common';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 2.75 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  linkOpen: {
    width: sidebarConfig.drawerWidthClosed,
  },
  linkClose: {
    marginLeft: 24
  }
});

const SidebarLogo = () => {

  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={isOpen ? classes.linkOpen : classes.linkClose} aria-label="Home">
        {isOpen ? <TotvsLogoFull color='#fff' /> : <TotvsLogoIcon color='#fff'/>}
      </Link>
    </div>
  );
};

const useSidebarDividerStyles = makeStyles({
  root: {
    backgroundColor: '#606060',
  },
});

const SidebarDividerStyled = () => {
  const classes = useSidebarDividerStyles();
  return <SidebarDivider className={classes.root} />;
};

export const Root = ({ children }: PropsWithChildren<{}>) => {

  return (<SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="" text="Home" />
        <MyGroupsSidebarItem singularTitle="My Group" pluralTitle="My Groups" icon={GroupIcon} filter={{'spec.type': ['business-unit', 'team']}} />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <RequirePermission permission={openControlReadPermission} errorPage={<></>}>
          <SidebarItem icon={DomainIcon} text="Workspaces">
            <SidebarSubmenu title="Environment">
              <SidebarSubmenuItem title="Development" to="workspaces/development" icon={BugReportIcon} />
              <SidebarSubmenuItem title="Staging" to="workspaces/staging" icon={BuildIcon} />
              <SidebarSubmenuItem title="Production" to="workspaces/production" icon={DomainIcon} />
            </SidebarSubmenu>
          </SidebarItem>
        </RequirePermission>
        <SidebarItem icon={LibraryBooksIcon} to="docs" text="Docs" />
        <RequirePermission permission={requestManagerReadPermission} errorPage={<></>}>
          <SidebarItem icon={AssignmentIcon} to="requests" text="Requests" />
        </RequirePermission>
        {/* End global nav */}
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDividerStyled />
      <SidebarItem icon={FeedbackIcon} to="feedback" text="Help Us Improve" />
      <SidebarGroup label="Settings" icon={<UserSettingsSignInAvatar />} to="/settings">
      <SidebarItem icon={HistoryIcon} to="/activity-history" text="Activity History" />
      <Administration />
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>);
}
